// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { removeBackground } from '@imgly/background-removal';
import { CloseIcon, BrushIcon } from './Icons';
import { useAuth } from '../contexts/AuthContext';

interface BackgroundRemoverModalProps {
    imageUrl: string;
    onSave: (newUrl: string) => void;
    onCancel: () => void;
}

export const BackgroundRemoverModal: React.FC<BackgroundRemoverModalProps> = ({ imageUrl, onSave, onCancel }) => {
    const { currentUser, usersData } = useAuth();
    const currentUserData = usersData[currentUser] || {};
    const baseColor = currentUserData?.appThemeColor || '#3b82f6';
    const textColor = currentUserData?.appTextColor || '#ffffff';
    const opacityVal = (currentUserData?.postBlurOpacity ?? 50) / 100;
    
    const modalBg = `rgba(${parseInt(baseColor.slice(1,3), 16)}, ${parseInt(baseColor.slice(3,5), 16)}, ${parseInt(baseColor.slice(5,7), 16)}, ${opacityVal})`;

    const [isProcessing, setIsProcessing] = useState(true);
    const [mode, setMode] = useState<'preview' | 'edit'>('preview');
    const [brushMode, setBrushMode] = useState<'erase' | 'add'>('erase');
    const [brushSize, setBrushSize] = useState(20);
    
    const [error, setError] = useState<string | null>(null);

    const containerRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const originalCanvasRef = useRef<HTMLCanvasElement>(null);
    const scratchCanvasRef = useRef<HTMLCanvasElement>(null);

    const [imgDimensions, setImgDimensions] = useState({ width: 0, height: 0 });

    const isDrawing = useRef(false);
    const lastPos = useRef({ x: 0, y: 0 });

    useEffect(() => {
        const processImage = async () => {
            try {
                // Load original image to get dimensions and prepare restoration canvas
                const originalImg = new Image();
                originalImg.crossOrigin = 'anonymous';
                
                await new Promise((resolve, reject) => {
                    originalImg.onload = resolve;
                    originalImg.onerror = reject;
                    originalImg.src = imageUrl;
                });

                const width = originalImg.width;
                const height = originalImg.height;
                setImgDimensions({ width, height });

                // Set up original canvas for "Add" mode
                if (originalCanvasRef.current && scratchCanvasRef.current) {
                    originalCanvasRef.current.width = width;
                    originalCanvasRef.current.height = height;
                    const oCtx = originalCanvasRef.current.getContext('2d');
                    if (oCtx) oCtx.drawImage(originalImg, 0, 0);

                    scratchCanvasRef.current.width = width;
                    scratchCanvasRef.current.height = height;
                }

                // Process background removal
                const config = {
                    publicPath: 'https://static.remove.bg/remove-bg-web/', // Use local models if possible, but imgly fetches from cdn automatically
                    debug: false
                };
                
                const blob = await removeBackground(imageUrl, config);
                const resultUrl = URL.createObjectURL(blob);

                const resultImg = new Image();
                await new Promise((resolve, reject) => {
                    resultImg.onload = resolve;
                    resultImg.onerror = reject;
                    resultImg.src = resultUrl;
                });

                // Draw result to main canvas
                if (canvasRef.current) {
                    canvasRef.current.width = width;
                    canvasRef.current.height = height;
                    const ctx = canvasRef.current.getContext('2d');
                    if (ctx) ctx.drawImage(resultImg, 0, 0);
                }

                setIsProcessing(false);
            } catch (err) {
                console.error("BG Removal Error:", err);
                setError("Failed to process image.");
                setIsProcessing(false);
            }
        };

        processImage();
    }, [imageUrl]);

    const getCanvasPos = (e: React.MouseEvent | React.TouchEvent) => {
        if (!canvasRef.current) return { x: 0, y: 0 };
        const rect = canvasRef.current.getBoundingClientRect();
        
        // Calculate the scale between the actual canvas pixels and the displayed CSS size
        const scaleX = canvasRef.current.width / rect.width;
        const scaleY = canvasRef.current.height / rect.height;

        let clientX, clientY;
        if ('touches' in e) {
            clientX = e.touches[0].clientX;
            clientY = e.touches[0].clientY;
        } else {
            clientX = e.clientX;
            clientY = e.clientY;
        }

        return {
            x: (clientX - rect.left) * scaleX,
            y: (clientY - rect.top) * scaleY
        };
    };

    const handleDrawStart = (e: React.MouseEvent | React.TouchEvent) => {
        if (mode !== 'edit') return;
        e.preventDefault();
        isDrawing.current = true;
        const pos = getCanvasPos(e);
        lastPos.current = pos;
        drawStroke(pos.x, pos.y, pos.x, pos.y);
    };

    const handleDrawMove = (e: React.MouseEvent | React.TouchEvent) => {
        if (!isDrawing.current || mode !== 'edit') return;
        e.preventDefault();
        const pos = getCanvasPos(e);
        drawStroke(lastPos.current.x, lastPos.current.y, pos.x, pos.y);
        lastPos.current = pos;
    };

    const handleDrawEnd = () => {
        isDrawing.current = false;
    };

    const drawStroke = (x1: number, y1: number, x2: number, y2: number) => {
        const ctx = canvasRef.current?.getContext('2d');
        if (!ctx) return;

        if (brushMode === 'erase') {
            ctx.globalCompositeOperation = 'destination-out';
            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.lineWidth = brushSize;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.stroke();
            
            // Draw a dot if just clicking
            if (x1 === x2 && y1 === y2) {
                ctx.beginPath();
                ctx.arc(x1, y1, brushSize / 2, 0, Math.PI * 2);
                ctx.fill();
            }
            
            ctx.globalCompositeOperation = 'source-over';
        } else if (brushMode === 'add') {
            const sCanvas = scratchCanvasRef.current;
            const oCanvas = originalCanvasRef.current;
            if (!sCanvas || !oCanvas) return;

            const sCtx = sCanvas.getContext('2d');
            if (!sCtx) return;

            // Clear scratch canvas
            sCtx.clearRect(0, 0, sCanvas.width, sCanvas.height);
            
            // Draw stroke on scratch canvas
            sCtx.globalCompositeOperation = 'source-over';
            sCtx.beginPath();
            sCtx.moveTo(x1, y1);
            sCtx.lineTo(x2, y2);
            sCtx.lineWidth = brushSize;
            sCtx.lineCap = 'round';
            sCtx.lineJoin = 'round';
            sCtx.strokeStyle = 'black';
            sCtx.stroke();
            
            if (x1 === x2 && y1 === y2) {
                sCtx.beginPath();
                sCtx.arc(x1, y1, brushSize / 2, 0, Math.PI * 2);
                sCtx.fillStyle = 'black';
                sCtx.fill();
            }

            // Clip original image into the stroke
            sCtx.globalCompositeOperation = 'source-in';
            sCtx.drawImage(oCanvas, 0, 0);

            // Draw restored pixels onto main canvas
            ctx.globalCompositeOperation = 'source-over';
            ctx.drawImage(sCanvas, 0, 0);
        }
    };

    const handleSave = () => {
        if (!canvasRef.current) return;
        const dataUrl = canvasRef.current.toDataURL('image/png');
        onSave(dataUrl);
    };

    return (
        <div className="fixed inset-0 z-[200] flex flex-col animate-in fade-in duration-300" style={{ backgroundColor: modalBg, color: textColor, backdropFilter: 'blur(20px)' }}>
            {/* Header */}
            <div className="flex items-center justify-between p-4 bg-black/20 border-b border-white/10 shrink-0 mt-[var(--safe-area-top,24px)]">
                <button onClick={onCancel} className="p-2 rounded-full hover:bg-white/10 transition-colors">
                    <CloseIcon className="w-6 h-6" />
                </button>
                <div className="font-bold text-lg" style={{ color: baseColor, textShadow: '0 2px 10px rgba(0,0,0,0.5)' }}>
                    AI Cutout
                </div>
                <button 
                    onClick={handleSave}
                    disabled={isProcessing}
                    className={`px-4 py-1.5 rounded-full font-bold text-sm transition-all ${isProcessing ? 'opacity-50 cursor-not-allowed bg-black/20' : 'bg-white text-black hover:scale-105 shadow-lg'}`}
                >
                    Save
                </button>
            </div>

            {/* Workspace */}
            <div className="flex-1 relative overflow-hidden flex flex-col items-center justify-center p-4" ref={containerRef}>
                {isProcessing ? (
                    <div className="flex flex-col items-center gap-4">
                        <div className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: baseColor, borderTopColor: 'transparent' }} />
                        <span className="font-bold text-lg animate-pulse shadow-sm">Isolating Subject...</span>
                        <span className="text-xs opacity-70">This may take a moment</span>
                    </div>
                ) : error ? (
                    <div className="flex flex-col items-center gap-4 text-red-400">
                        <span className="font-bold text-lg">Error</span>
                        <span className="text-sm text-center max-w-xs">{error}</span>
                    </div>
                ) : (
                    <div className="relative w-full h-full flex items-center justify-center">
                        <div className="relative shadow-2xl rounded-2xl overflow-hidden bg-checkered" style={{ maxWidth: '100%', maxHeight: '100%' }}>
                            <canvas
                                ref={canvasRef}
                                className={`block w-full h-auto object-contain ${mode === 'edit' ? 'cursor-crosshair touch-none' : ''}`}
                                style={{ maxHeight: 'calc(100vh - 200px)' }}
                                onMouseDown={handleDrawStart}
                                onMouseMove={handleDrawMove}
                                onMouseUp={handleDrawEnd}
                                onMouseLeave={handleDrawEnd}
                                onTouchStart={handleDrawStart}
                                onTouchMove={handleDrawMove}
                                onTouchEnd={handleDrawEnd}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Hidden Canvases */}
            <canvas ref={originalCanvasRef} className="hidden" />
            <canvas ref={scratchCanvasRef} className="hidden" />

            {/* Toolbar */}
            {!isProcessing && !error && (
                <div className="bg-black/30 border-t border-white/10 p-4 pb-[calc(1rem+var(--safe-area-bottom,0px))] flex flex-col gap-4 shrink-0">
                    
                    {mode === 'preview' ? (
                        <div className="flex justify-center">
                            <button 
                                onClick={() => setMode('edit')}
                                className="flex items-center gap-2 px-6 py-3 rounded-2xl bg-white/10 hover:bg-white/20 transition-all font-bold"
                            >
                                <BrushIcon className="w-5 h-5" />
                                Refine Cutout
                            </button>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-4 w-full max-w-md mx-auto">
                            {/* Mode Toggles */}
                            <div className="flex bg-black/40 p-1 rounded-2xl border border-white/5">
                                <button
                                    onClick={() => setBrushMode('erase')}
                                    className={`flex-1 py-2 rounded-xl font-bold text-sm transition-all ${brushMode === 'erase' ? 'bg-white text-black shadow-md' : 'text-white/60 hover:text-white'}`}
                                >
                                    Erase
                                </button>
                                <button
                                    onClick={() => setBrushMode('add')}
                                    className={`flex-1 py-2 rounded-xl font-bold text-sm transition-all ${brushMode === 'add' ? 'bg-white text-black shadow-md' : 'text-white/60 hover:text-white'}`}
                                >
                                    Restore
                                </button>
                            </div>

                            {/* Brush Size */}
                            <div className="flex items-center gap-4 bg-black/20 p-4 rounded-2xl border border-white/5">
                                <span className="text-xs font-bold text-white/50 w-8">Size</span>
                                <input 
                                    type="range" 
                                    min="5" 
                                    max="100" 
                                    value={brushSize} 
                                    onChange={(e) => setBrushSize(parseInt(e.target.value))}
                                    className="flex-1"
                                />
                                <div className="w-8 flex justify-center">
                                    <div className="bg-white rounded-full" style={{ width: Math.max(4, brushSize / 4), height: Math.max(4, brushSize / 4) }} />
                                </div>
                            </div>
                            
                            <button 
                                onClick={() => setMode('preview')}
                                className="mt-2 py-3 rounded-2xl bg-white/5 hover:bg-white/10 transition-all font-bold text-sm"
                            >
                                Done Refining
                            </button>
                        </div>
                    )}
                </div>
            )}
            
            <style>{`
                .bg-checkered {
                    background-color: #111;
                    background-image: linear-gradient(45deg, #1a1a1a 25%, transparent 25%),
                        linear-gradient(-45deg, #1a1a1a 25%, transparent 25%),
                        linear-gradient(45deg, transparent 75%, #1a1a1a 75%),
                        linear-gradient(-45deg, transparent 75%, #1a1a1a 75%);
                    background-size: 20px 20px;
                    background-position: 0 0, 0 10px, 10px -10px, -10px 0px;
                }
            `}</style>
        </div>
    );
};
